// ============================================================================
// Lead phone helpers — the ONE place that knows a lead's number can live in
// either field.
// ============================================================================
// Since the landing's contact form gained the "este número tiene WhatsApp"
// checkbox, a lead's number lands in `whatsapp` OR in `phone`. The backend
// already treats them as interchangeable (the AI-call orchestrator dials
// `lead.whatsapp || lead.phone`, and POST /crm/calls/place rejects only when
// BOTH are missing), so every UI surface must do the same — otherwise a
// phone-only lead is dialable by the API but invisible / uncallable in the
// screens reps actually work in.
// ============================================================================

import type { Lead } from "./leadsService";

/** Anything that carries the two phone channels (a lead, a booking, a form). */
export interface PhoneChannels {
  whatsapp?: string | null;
  phone?: string | null;
}

/** A lead's dialable number: the WhatsApp line if any, else the plain phone. */
export function leadPhone(lead: PhoneChannels): string | null {
  return lead.whatsapp || lead.phone || null;
}

/** True when the platform can actually place a call to this lead. */
export function isLeadCallable(lead: PhoneChannels): boolean {
  return Boolean(leadPhone(lead));
}

/** True when the number we hold for this lead is a WhatsApp line. */
export function hasWhatsappLine(lead: PhoneChannels): boolean {
  return Boolean(lead.whatsapp);
}

/**
 * Split the single number a CRM form collects into the channel it belongs to —
 * the console mirror of the landing's `lib/phoneChannels`. A number the rep said
 * has NO WhatsApp may never be stored as `whatsapp` (the UI would offer a wa.me/
 * conversation on a line that cannot receive it).
 */
export function phoneChannels(value: string, hasWhatsapp: boolean): { whatsapp: string; phone: string } {
  const v = (value || "").trim();
  if (!v) return { whatsapp: "", phone: "" };
  return hasWhatsapp ? { whatsapp: v, phone: "" } : { whatsapp: "", phone: v };
}

/** The inverse: how a stored lead/booking prefills that single form field. */
export function phoneChannelOf(entity: PhoneChannels): { value: string; hasWhatsapp: boolean } {
  if (entity.whatsapp) return { value: entity.whatsapp, hasWhatsapp: true };
  if (entity.phone) return { value: entity.phone, hasWhatsapp: false };
  return { value: "", hasWhatsapp: true };
}

/** Fields the Leads inbox free-text search looks through. */
export function leadSearchValues(lead: Lead): (string | null | undefined)[] {
  return [
    lead.name,
    lead.company,
    lead.email,
    lead.whatsapp,
    lead.phone,
    lead.system,
    lead.interestService,
    lead.acquisitionSource,
    lead.referralName,
    typeof lead.product === "string" ? lead.product : null,
  ];
}

/**
 * Free-text match for the Leads inbox. An empty/blank query matches everything.
 * Matching is case-insensitive and substring-based, and includes the plain
 * `phone` so a prospect who called back from a non-WhatsApp number can be found.
 */
export function matchesLeadQuery(lead: Lead, query: string): boolean {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return leadSearchValues(lead)
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(q));
}
